/**
 * Usage Service - Handles all usage tracking and limit management API operations
 */
import axios from 'axios';
import API_CONFIG from '../config/api.config';
import { getAuthHeader } from './authService';

const API_BASE = API_CONFIG.BASE_URL;

/**
 * Get usage summary for current tenant
 * @returns {Promise} Promise with usage summary data
 */
export const getUsageSummary = async () => {
  try {
    const response = await axios.get(
      `${API_BASE}${API_CONFIG.ENDPOINTS.USAGE_SUMMARY}`,
      {
        headers: getAuthHeader()
      }
    );
    return response.data;
  } catch (error) {
    console.error('Get usage summary error:', error);
    throw handleApiError(error);
  }
};

/**
 * Get usage limits for current tenant
 * @returns {Promise} Promise with usage limits data
 */
export const getUsageLimits = async () => {
  try {
    const response = await axios.get(
      `${API_BASE}${API_CONFIG.ENDPOINTS.USAGE_LIMITS}`,
      {
        headers: getAuthHeader()
      }
    );
    return response.data;
  } catch (error) {
    console.error('Get usage limits error:', error);
    throw handleApiError(error);
  }
};

/**
 * Get usage history for a specific metric
 * @param {string} metricType - Type of metric (USERS, LOCATIONS, PRODUCTS, ORDERS, STORAGE_MB)
 * @param {number} months - Number of months to retrieve
 * @returns {Promise} Promise with usage history data
 */
export const getUsageHistory = async (metricType, months = 6) => {
  try {
    const response = await axios.get(
      `${API_BASE}${API_CONFIG.ENDPOINTS.USAGE_HISTORY}`,
      {
        params: { metricType, months },
        headers: getAuthHeader()
      }
    );
    return response.data;
  } catch (error) {
    console.error('Get usage history error:', error);
    throw handleApiError(error);
  }
};

/**
 * Validate if an action can be performed within usage limits
 * @param {string} metricType - Type of metric to validate
 * @param {number} additionalCount - Additional usage count to validate
 * @returns {Promise} Promise with validation result
 */
export const validateUsage = async (metricType, additionalCount = 1) => {
  try {
    const response = await axios.post(
      `${API_BASE}${API_CONFIG.ENDPOINTS.USAGE_VALIDATE}`,
      {
        metricType,
        additionalCount
      },
      {
        headers: getAuthHeader()
      }
    );
    return response.data;
  } catch (error) {
    console.error('Validate usage error:', error);
    throw handleApiError(error);
  }
};

/**
 * Get usage analytics (admin only)
 * @returns {Promise} Promise with usage analytics data
 */
export const getUsageAnalytics = async () => {
  try {
    const response = await axios.get(
      `${API_BASE}${API_CONFIG.ENDPOINTS.USAGE_ANALYTICS}`,
      {
        headers: getAuthHeader()
      }
    );
    return response.data;
  } catch (error) {
    console.error('Get usage analytics error:', error);
    throw handleApiError(error);
  }
};

/**
 * Get current usage for all metrics
 * @returns {Promise} Promise with current usage data
 */
export const getCurrentUsage = async () => {
  try {
    const response = await axios.get(
      `${API_BASE}/usage/current`,
      {
        headers: getAuthHeader()
      }
    );
    return response.data;
  } catch (error) {
    console.error('Get current usage error:', error);
    throw handleApiError(error);
  }
};

/**
 * Get usage percentages for all metrics
 * @returns {Promise} Promise with usage percentages
 */
export const getUsagePercentages = async () => {
  try {
    const response = await axios.get(
      `${API_BASE}/usage/percentages`,
      {
        headers: getAuthHeader()
      }
    );
    return response.data;
  } catch (error) {
    console.error('Get usage percentages error:', error);
    throw handleApiError(error);
  }
};

/**
 * Get tenants approaching limits (admin only)
 * @returns {Promise} Promise with tenants approaching limits
 */
export const getTenantsApproachingLimits = async () => {
  try {
    const response = await axios.get(
      `${API_BASE}/usage/approaching-limits`,
      {
        headers: getAuthHeader()
      }
    );
    return response.data;
  } catch (error) {
    console.error('Get tenants approaching limits error:', error);
    throw handleApiError(error);
  }
};

/**
 * Get tenants exceeding limits (admin only)
 * @returns {Promise} Promise with tenants exceeding limits
 */
export const getTenantsExceedingLimits = async () => {
  try {
    const response = await axios.get(
      `${API_BASE}/usage/exceeding-limits`,
      {
        headers: getAuthHeader()
      }
    );
    return response.data;
  } catch (error) {
    console.error('Get tenants exceeding limits error:', error);
    throw handleApiError(error);
  }
};

/**
 * Reset monthly usage for tenant (admin only)
 * @param {string} tenantId - Tenant ID
 * @returns {Promise} Promise with reset result
 */
export const resetMonthlyUsage = async (tenantId) => {
  try {
    const response = await axios.post(
      `${API_BASE}/usage/reset-monthly/${tenantId}`,
      null,
      {
        headers: getAuthHeader()
      }
    );
    return response.data;
  } catch (error) {
    console.error('Reset monthly usage error:', error);
    throw handleApiError(error);
  }
};

/**
 * Recalculate usage for tenant (admin only)
 * @param {string} tenantId - Tenant ID
 * @returns {Promise} Promise with recalculation result
 */
export const recalculateUsage = async (tenantId) => {
  try {
    const response = await axios.post(
      `${API_BASE}/usage/recalculate/${tenantId}`,
      null,
      {
        headers: getAuthHeader()
      }
    );
    return response.data;
  } catch (error) {
    console.error('Recalculate usage error:', error);
    throw handleApiError(error);
  }
};

/**
 * Bulk update usage (admin only)
 * @param {Object} usageUpdates - Bulk usage updates
 * @returns {Promise} Promise with bulk update result
 */
export const bulkUpdateUsage = async (usageUpdates) => {
  try {
    const response = await axios.post(
      `${API_BASE}/usage/bulk-update`,
      usageUpdates,
      {
        headers: getAuthHeader()
      }
    );
    return response.data;
  } catch (error) {
    console.error('Bulk update usage error:', error);
    throw handleApiError(error);
  }
};

/**
 * Handle API errors consistently
 * @param {Error} error - Axios error object
 * @returns {Error} Formatted error
 */
const handleApiError = (error) => {
  if (error.response) {
    const { status, data } = error.response;
    const message = data?.message || data?.error || 'An error occurred';
    
    switch (status) {
      case 400:
        return new Error(`Invalid request: ${message}`);
      case 401:
        return new Error('Authentication required');
      case 403:
        return new Error('Access forbidden');
      case 404:
        return new Error('Resource not found');
      case 409:
        return new Error(`Conflict: ${message}`);
      case 429:
        return new Error('Usage limit exceeded. Please upgrade your plan.');
      case 500:
        return new Error('Server error. Please try again later.');
      default:
        return new Error(`Error (${status}): ${message}`);
    }
  } else if (error.request) {
    return new Error('Network error. Please check your connection.');
  } else {
    return new Error(`Request error: ${error.message}`);
  }
};

// Export all functions as default object
const usageService = {
  getUsageSummary,
  getUsageLimits,
  getUsageHistory,
  validateUsage,
  getUsageAnalytics,
  getCurrentUsage,
  getUsagePercentages,
  getTenantsApproachingLimits,
  getTenantsExceedingLimits,
  resetMonthlyUsage,
  recalculateUsage,
  bulkUpdateUsage
};

export default usageService;
